export default {
  // Breakpoints for responsive design
  breakpoints: ['640px', '768px', '1024px', '1280px', '1536px'],

  // Color palette - HP-MAP branding
  colors: {
    // Primary brand colors
    primary: '#0047AB',        // Deep blue (trust, professionalism)
    secondary: '#DC143C',      // Crimson red (energy, impact)
    accent: '#00A86B',         // Jade green (growth, development)

    // Neutrals
    text: '#2C3E50',           // Dark slate for body text
    textLight: '#6B7280',      // Gray for secondary text
    background: '#FFFFFF',     // White background
    backgroundAlt: '#F8FAFC',  // Light gray background
    muted: '#E5E7EB',          // Muted gray

    // Semantic colors
    success: '#10B981',        // Green for success states
    warning: '#F59E0B',        // Orange for warnings
    error: '#EF4444',          // Red for errors
    info: '#3B82F6',           // Blue for info

    // UI elements
    border: '#D1D5DB',         // Border color
    cardBg: '#FFFFFF',         // Card background
    headerBg: '#FFFFFF',       // Header background
    footerBg: '#1F2937',       // Dark footer background
    footerText: '#F3F4F6',     // Light footer text

    // Interactive states
    primaryHover: '#003A8C',   // Darker blue on hover
    secondaryHover: '#B01030', // Darker red on hover
  },

  // Typography
  fonts: {
    body: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    heading: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    monospace: 'Menlo, Monaco, "Courier New", monospace',
  },

  // Font sizes - granular scale from 12px to 72px
  fontSizes: [
    12,  // 0 - caption
    14,  // 1 - small
    16,  // 2 - body
    18,  // 3 - lead
    20,  // 4 - h5
    24,  // 5 - h4
    32,  // 6 - h3
    40,  // 7 - h2
    48,  // 8 - h1
    64,  // 9 - hero
    72,  // 10 - display
  ],

  // Font weights
  fontWeights: {
    light: 300,
    body: 400,
    medium: 500,
    semibold: 600,
    heading: 700,
    bold: 700,
    black: 900,
  },

  // Line heights
  lineHeights: {
    none: 1,
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
    body: 1.625,
    heading: 1.2,
  },

  // Spacing scale - fine-grained from 0 to 256px
  space: [
    0,    // 0
    4,    // 1
    8,    // 2
    12,   // 3
    16,   // 4
    24,   // 5
    32,   // 6
    48,   // 7
    64,   // 8
    96,   // 9
    128,  // 10
    192,  // 11
    256,  // 12
  ],

  // Layout sizes
  sizes: {
    container: '1280px',       // Standard container
    containerWide: '1440px',   // Wide container
    containerNarrow: '960px',  // Narrow container
    maxContent: '720px',       // Max content width for readability
    full: '100%',
  },

  // Border radius
  radii: {
    none: 0,
    sm: '4px',
    default: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    full: '9999px',
    circle: '50%',
  },

  // Shadows
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    default: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    card: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },

  // Borders
  borders: {
    none: 'none',
    thin: '1px solid',
    medium: '2px solid',
    thick: '4px solid',
  },

  // Transitions
  transitions: {
    fast: '150ms ease-in-out',
    base: '250ms ease-in-out',
    slow: '350ms ease-in-out',
    slower: '500ms ease-in-out',
  },

  // Animation keyframes
  animations: {
    fadeIn: {
      '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      animation: 'fadeIn 0.6s ease-in',
    },
    slideUp: {
      '@keyframes slideUp': {
        from: {
          opacity: 0,
          transform: 'translateY(30px)'
        },
        to: {
          opacity: 1,
          transform: 'translateY(0)'
        },
      },
      animation: 'slideUp 0.6s ease-out',
    },
    slideInLeft: {
      '@keyframes slideInLeft': {
        from: {
          opacity: 0,
          transform: 'translateX(-30px)'
        },
        to: {
          opacity: 1,
          transform: 'translateX(0)'
        },
      },
      animation: 'slideInLeft 0.6s ease-out',
    },
    slideInRight: {
      '@keyframes slideInRight': {
        from: {
          opacity: 0,
          transform: 'translateX(30px)'
        },
        to: {
          opacity: 1,
          transform: 'translateX(0)'
        },
      },
      animation: 'slideInRight 0.6s ease-out',
    },
    scaleIn: {
      '@keyframes scaleIn': {
        from: {
          opacity: 0,
          transform: 'scale(0.9)'
        },
        to: {
          opacity: 1,
          transform: 'scale(1)'
        },
      },
      animation: 'scaleIn 0.5s ease-out',
    },
    pulse: {
      '@keyframes pulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.7 },
      },
      animation: 'pulse 2s ease-in-out infinite',
    },
  },

  // Text variants
  text: {
    heading: {
      fontFamily: 'heading',
      lineHeight: 'heading',
      fontWeight: 'heading',
      color: 'text',
    },
    display: {
      fontFamily: 'heading',
      fontWeight: 'black',
      lineHeight: 'tight',
      fontSize: [8, 9, 10],
    },
    caps: {
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontWeight: 'bold',
    },
    body: {
      fontFamily: 'body',
      lineHeight: 'body',
      fontWeight: 'body',
      color: 'text',
    },
    lead: {
      fontSize: 3,
      lineHeight: 'relaxed',
      color: 'textLight',
    },
  },

  // Button variants
  buttons: {
    primary: {
      color: 'white',
      bg: 'primary',
      fontWeight: 'semibold',
      borderRadius: 'default',
      px: 6,
      py: 3,
      cursor: 'pointer',
      transition: 'base',
      '&:hover': {
        bg: 'primaryHover',
        transform: 'translateY(-2px)',
        boxShadow: 'md',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
    },
    secondary: {
      color: 'white',
      bg: 'secondary',
      fontWeight: 'semibold',
      borderRadius: 'default',
      px: 6,
      py: 3,
      cursor: 'pointer',
      transition: 'base',
      '&:hover': {
        bg: 'secondaryHover',
        transform: 'translateY(-2px)',
        boxShadow: 'md',
      },
    },
    outline: {
      color: 'primary',
      bg: 'transparent',
      border: 'medium',
      borderColor: 'primary',
      fontWeight: 'semibold',
      borderRadius: 'default',
      px: 6,
      py: 3,
      cursor: 'pointer',
      transition: 'base',
      '&:hover': {
        bg: 'primary',
        color: 'white',
        borderColor: 'primary',
      },
    },
    text: {
      color: 'primary',
      bg: 'transparent',
      fontWeight: 'semibold',
      px: 4,
      py: 2,
      cursor: 'pointer',
      transition: 'base',
      '&:hover': {
        color: 'primaryHover',
        textDecoration: 'underline',
      },
    },
    large: {
      variant: 'buttons.primary',
      fontSize: 3,
      px: 8,
      py: 4,
    },
    small: {
      variant: 'buttons.primary',
      fontSize: 1,
      px: 4,
      py: 2,
    },
  },

  // Card variants
  cards: {
    primary: {
      bg: 'cardBg',
      borderRadius: 'default',
      boxShadow: 'card',
      p: 6,
      transition: 'base',
      '&:hover': {
        boxShadow: 'lg',
        transform: 'translateY(-4px)',
      },
    },
    compact: {
      variant: 'cards.primary',
      p: 4,
    },
    elevated: {
      variant: 'cards.primary',
      boxShadow: 'lg',
    },
  },

  // Form styles
  forms: {
    input: {
      borderColor: 'border',
      borderRadius: 'default',
      px: 4,
      py: 3,
      fontSize: 2,
      fontFamily: 'body',
      outline: 'none',
      transition: 'base',
      '&:focus': {
        borderColor: 'primary',
        boxShadow: '0 0 0 3px rgba(0, 71, 171, 0.1)',
      },
    },
    textarea: {
      variant: 'forms.input',
      fontFamily: 'body',
    },
    label: {
      fontSize: 2,
      fontWeight: 'medium',
      mb: 2,
      color: 'text',
    },
    select: {
      variant: 'forms.input',
    },
  },

  // Link styles
  links: {
    nav: {
      color: 'text',
      fontWeight: 'medium',
      textDecoration: 'none',
      transition: 'fast',
      '&:hover': {
        color: 'primary',
      },
      '&.active': {
        color: 'primary',
        fontWeight: 'semibold',
      },
    },
    footer: {
      color: 'footerText',
      textDecoration: 'none',
      transition: 'fast',
      '&:hover': {
        color: 'white',
        textDecoration: 'underline',
      },
    },
  },

  // Layout styles
  layout: {
    container: {
      maxWidth: 'container',
      mx: 'auto',
      px: [4, 5, 6],
    },
    header: {
      bg: 'headerBg',
      borderBottom: 'thin',
      borderColor: 'border',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      boxShadow: 'sm',
    },
    footer: {
      bg: 'footerBg',
      color: 'footerText',
      py: 8,
    },
  },

  // Utility styles
  styles: {
    root: {
      fontFamily: 'body',
      lineHeight: 'body',
      fontWeight: 'body',
      color: 'text',
      bg: 'background',
    },
    h1: {
      variant: 'text.heading',
      fontSize: 8,
      mt: 0,
      mb: 4,
    },
    h2: {
      variant: 'text.heading',
      fontSize: 7,
      mt: 5,
      mb: 3,
    },
    h3: {
      variant: 'text.heading',
      fontSize: 6,
      mt: 4,
      mb: 3,
    },
    h4: {
      variant: 'text.heading',
      fontSize: 5,
      mt: 4,
      mb: 2,
    },
    h5: {
      variant: 'text.heading',
      fontSize: 4,
      mt: 3,
      mb: 2,
    },
    h6: {
      variant: 'text.heading',
      fontSize: 3,
      mt: 3,
      mb: 2,
    },
    p: {
      fontSize: 2,
      lineHeight: 'body',
      mb: 4,
    },
    a: {
      color: 'primary',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    ul: {
      pl: 5,
      mb: 4,
    },
    ol: {
      pl: 5,
      mb: 4,
    },
    li: {
      mb: 2,
    },
    blockquote: {
      borderLeft: 'thick',
      borderColor: 'primary',
      pl: 5,
      py: 2,
      fontStyle: 'italic',
      color: 'textLight',
    },
    pre: {
      fontFamily: 'monospace',
      overflowX: 'auto',
      bg: 'muted',
      p: 4,
      borderRadius: 'default',
      fontSize: 1,
    },
    code: {
      fontFamily: 'monospace',
      fontSize: '0.9em',
      bg: 'muted',
      px: 1,
      py: '2px',
      borderRadius: 'sm',
    },
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    th: {
      textAlign: 'left',
      borderBottomStyle: 'solid',
      borderBottomWidth: '2px',
      borderBottomColor: 'border',
      verticalAlign: 'bottom',
      p: 3,
    },
    td: {
      textAlign: 'left',
      borderBottomStyle: 'solid',
      borderBottomWidth: '1px',
      borderBottomColor: 'border',
      verticalAlign: 'top',
      p: 3,
    },
    hr: {
      border: 'none',
      borderBottom: 'thin',
      borderColor: 'border',
      my: 6,
    },
  },
}
